import { useMemo } from 'react';
import PropTypes from 'prop-types';

import { cn } from '#/lib/utils';
import useDetailTable from '#/hooks/table/useDetailTable';
import DetailTable from '#/modules/commonComponents/table/DetailTable';
import {
  displayOrDash,
  getSalesOrderLineNetAmount,
  getSalesOrderLineUnitPrice,
} from '#/modules/crmMgmt/salesOrder/helper/mapSalesOrderMeta';
import {
  formatDisplayAmount,
  formatDisplayQty,
} from '#/utils/formatNumber';

function formatProductLabel(product) {
  if (!product) {
    return '—';
  }
  const code = String(product.productNo ?? product.code ?? '').trim();
  const name = product.name ? String(product.name).trim() : '';
  if (code && name) {
    return `${code} ${name}`;
  }
  return code || name || '—';
}

function buildLineColumns() {
  return [
    {
      id: 'product',
      header: '產品',
      accessorFn: (row) => formatProductLabel(row.product),
      size: 220,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
    },
    {
      id: 'qty',
      header: '數量',
      accessorFn: (row) => formatDisplayQty(row.qty ?? row.shipQty),
      size: 110,
      enableSorting: false,
    },
    {
      id: 'unitPrice',
      header: '單價',
      accessorFn: (row) => formatDisplayAmount(getSalesOrderLineUnitPrice(row)),
      size: 100,
      enableSorting: false,
    },
    {
      id: 'totalAmount',
      header: '小計',
      accessorFn: (row) => formatDisplayAmount(getSalesOrderLineNetAmount(row)),
      size: 110,
      enableSorting: false,
    },
    {
      id: 'remark',
      header: '備註',
      accessorFn: (row) => displayOrDash(row.remark),
      size: 160,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
    },
  ];
}

function buildManualLineColumns() {
  return [
    {
      id: 'itemName',
      header: '品名',
      accessorFn: (row) => displayOrDash(row.itemName),
      size: 220,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
    },
    {
      id: 'qty',
      header: '數量',
      accessorFn: (row) => formatDisplayQty(row.qty),
      size: 110,
      enableSorting: false,
    },
    {
      id: 'unitName',
      header: '單位',
      accessorFn: (row) => displayOrDash(row.unitName),
      size: 80,
      enableSorting: false,
    },
    {
      id: 'unitPrice',
      header: '單價',
      accessorFn: (row) => formatDisplayAmount(getSalesOrderLineUnitPrice(row)),
      size: 100,
      enableSorting: false,
    },
    {
      id: 'totalAmount',
      header: '小計',
      accessorFn: (row) => formatDisplayAmount(getSalesOrderLineNetAmount(row)),
      size: 110,
      enableSorting: false,
    },
    {
      id: 'remark',
      header: '備註',
      accessorFn: (row) => displayOrDash(row.remark),
      size: 160,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
    },
  ];
}

function ReferenceLineField({ label, value, numeric = false }) {
  return (
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p
        className={cn(
          'mt-0.5 text-sm font-medium text-gray-900',
          numeric && 'tabular-nums',
        )}
      >
        {value}
      </p>
    </div>
  );
}

ReferenceLineField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  numeric: PropTypes.bool,
};

function ReferenceLineRow({ line }) {
  return (
    <li className="py-3 first:pt-0">
      <div className="space-y-2">
        <ReferenceLineField
          label="產品"
          value={formatProductLabel(line?.product)}
        />
        <ReferenceLineField
          label="數量"
          value={formatDisplayQty(line?.qty ?? line?.shipQty)}
          numeric
        />
        <ReferenceLineField
          label="單價"
          value={formatDisplayAmount(getSalesOrderLineUnitPrice(line))}
          numeric
        />
        <ReferenceLineField
          label="小計"
          value={formatDisplayAmount(getSalesOrderLineNetAmount(line))}
          numeric
        />
        <ReferenceLineField
          label="備註"
          value={displayOrDash(line?.remark)}
        />
      </div>
    </li>
  );
}

ReferenceLineRow.propTypes = {
  line: PropTypes.object,
};

function ReferenceManualLineRow({ line }) {
  return (
    <li className="py-3 first:pt-0">
      <div className="space-y-2">
        <ReferenceLineField
          label="品名"
          value={displayOrDash(line?.itemName)}
        />
        <ReferenceLineField
          label="數量"
          value={formatDisplayQty(line?.qty)}
          numeric
        />
        <ReferenceLineField
          label="單位"
          value={displayOrDash(line?.unitName)}
        />
        <ReferenceLineField
          label="單價"
          value={formatDisplayAmount(getSalesOrderLineUnitPrice(line))}
          numeric
        />
        <ReferenceLineField
          label="小計"
          value={formatDisplayAmount(getSalesOrderLineNetAmount(line))}
          numeric
        />
        <ReferenceLineField
          label="備註"
          value={displayOrDash(line?.remark)}
        />
      </div>
    </li>
  );
}

ReferenceManualLineRow.propTypes = {
  line: PropTypes.object,
};

function SalesOrderLinesReferenceList({ lines = [], manualLines = [] }) {
  if (!lines.length && !manualLines.length) {
    return <p className="text-sm text-muted-foreground">尚無銷貨明細</p>;
  }

  return (
    <div className="space-y-4">
      {lines.length > 0 ? (
        <ul className="m-0 list-none divide-y divide-gray-300 p-0">
          {lines.map((line, index) => (
            <ReferenceLineRow
              key={line?.id ?? `so-line-${index}`}
              line={line}
            />
          ))}
        </ul>
      ) : null}
      {manualLines.length > 0 ? (
        <div>
          <p className="mb-2 text-xs font-medium text-muted-foreground">
            其他產品
          </p>
          <ul className="m-0 list-none divide-y divide-gray-300 p-0">
            {manualLines.map((line, index) => (
              <ReferenceManualLineRow
                key={line?.id ?? `so-manual-line-${index}`}
                line={line}
              />
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

SalesOrderLinesReferenceList.propTypes = {
  lines: PropTypes.arrayOf(PropTypes.object),
  manualLines: PropTypes.arrayOf(PropTypes.object),
};

function DetailTableBlock({ rows, columns, emptyLabel }) {
  const detailTableState = useDetailTable(rows, columns, {
    enableExpanding: false,
    getRowId: (row) => String(row.id),
  });

  if (rows.length === 0) {
    return <p className="px-1 py-3 text-sm text-muted-foreground">{emptyLabel}</p>;
  }

  return (
    <div className="detail-table-container">
      <DetailTable
        state={detailTableState}
        containerClassName="detail-table-scroll--section-embedded"
        tableClassName="detail-table--flat-lines"
      />
    </div>
  );
}

DetailTableBlock.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  emptyLabel: PropTypes.string.isRequired,
};

/**
 * 明細唯讀摘要（Detail 橫表；Edit 右欄 `layout="reference"` 直欄 divide）。
 * 產品 `lines` 與其他產品 `manualLines` 分開顯示。
 */
const SalesOrderLinesReadonlySection = ({
  lines = [],
  manualLines = [],
  embedded = false,
  layout = 'default',
}) => {
  const lineColumns = useMemo(() => buildLineColumns(), []);
  const manualColumns = useMemo(() => buildManualLineColumns(), []);
  const productRows = useMemo(
    () => (lines ?? []).map((line, index) => ({
      ...line,
      id: line?.id ?? `so-line-${index}`,
    })),
    [lines],
  );
  const manualRows = useMemo(
    () => (manualLines ?? []).map((line, index) => ({
      ...line,
      id: line?.id ?? `so-manual-line-${index}`,
    })),
    [manualLines],
  );

  const referenceContent = (
    <SalesOrderLinesReferenceList lines={lines} manualLines={manualLines} />
  );

  const hasAny = productRows.length > 0 || manualRows.length > 0;
  const tableContent = !hasAny ? (
    <p className="px-1 py-3 text-sm text-muted-foreground">尚無明細</p>
  ) : (
    <div className="space-y-4">
      {productRows.length > 0 ? (
        <DetailTableBlock
          rows={productRows}
          columns={lineColumns}
          emptyLabel="尚無產品明細"
        />
      ) : null}
      {manualRows.length > 0 ? (
        <div>
          <p className="mb-2 text-xs font-medium text-muted-foreground">
            其他產品
          </p>
          <DetailTableBlock
            rows={manualRows}
            columns={manualColumns}
            emptyLabel="尚無其他產品"
          />
        </div>
      ) : null}
    </div>
  );

  if (embedded && layout === 'reference') {
    return referenceContent;
  }

  if (embedded) {
    return tableContent;
  }

  return (
    <section className="pb-6">
      <div className="form-block">
        <h2 className="form-block__title">明細摘要</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          銷貨產品與數量一覽。
        </p>
      </div>
      {layout === 'reference' ? referenceContent : tableContent}
    </section>
  );
};

SalesOrderLinesReadonlySection.propTypes = {
  lines: PropTypes.arrayOf(PropTypes.object),
  manualLines: PropTypes.arrayOf(PropTypes.object),
  embedded: PropTypes.bool,
  layout: PropTypes.oneOf(['default', 'reference']),
};

export default SalesOrderLinesReadonlySection;
